import { ErrorMessage } from '@hookform/error-message'
import { Button, Checkbox, Flex, Form, Select, SelectProps, Tooltip, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import { useCallback } from 'react'
import { Control, Controller, UseFormClearErrors, UseFormResetField, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomSelect from 'src/app/shared/components/common/select'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { FormValues } from './type'

interface IProps {
  control: Control<FormValues>
  errors: any
  setValue: UseFormSetValue<FormValues>
  clientList: any[]
  resetField: UseFormResetField<FormValues>
  accountList?: { label: string; value: string }[]
  curentClient: any
  loading?: boolean
  clearErrors: UseFormClearErrors<FormValues>
}

const SelectClient = (props: IProps) => {
  const { t } = useTranslation()
  const { control, errors, clientList, accountList, curentClient, loading, resetField, clearErrors, setValue } = props
  const sharedProps: SelectProps = {
    mode: 'multiple',
    style: { width: '100%' },
    placeholder: t('ClientUserSetup:accountNames.placeholder'),
    maxTagCount: 'responsive',
  }
  const clientID = useWatch({
    control,
    name: 'clientName',
  })

  const filterOption = (input: string, option?: any) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const handleAssignAllExisting = useCallback(() => {
    clearErrors('accountNames')
    if (clientID) {
      setValue('accountNames', accountList?.map((item) => item.value) ?? [], {
        shouldDirty: true,
        shouldTouch: true,
      })
    } else {
      resetField('accountNames')
    }
  }, [accountList, clearErrors, clientID, resetField, setValue])

  const handleUnassignAllExisting = useCallback(() => {
    clearErrors('accountNames')
    setValue('accountNames', [], {
      shouldDirty: true,
      shouldTouch: true,
    })
  }, [clearErrors, setValue])

  return (
    <Flex vertical style={{ paddingBottom: '24px' }}>
      <Typography.Text style={{ fontSize: '18px', fontWeight: '700', lineHeight: '28px', paddingBottom: '4px' }}>
        {t('ClientUserSetup:selectClient')}
      </Typography.Text>

      <Flex vertical style={{ border: `1px solid ${colorBorderDisabled}`, padding: '16px', borderRadius: '16px' }}>
        <Form.Item label={t('ClientUserSetup:clientName.title')} className='isRequired'>
          <Controller
            name='clientName'
            control={control}
            render={({ field }) => (
              <Select
                status={errors?.clientName ? 'error' : ''}
                placeholder={t('ClientUserSetup:clientName.placeholder')}
                options={clientList}
                maxTagCount={'responsive'}
                onChange={(e) => {
                  setValue('accountNames', [])
                  field.onChange(e)
                }}
                onBlur={async (event) => {
                  field.onBlur()
                }}
                value={curentClient}
                showSearch
                filterOption={filterOption}
                filterSort={(a, b) => a.label.localeCompare(b.label)}
              />
            )}
          />
          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='clientName' />
          </Typography>
        </Form.Item>
        <Form.Item label={t('ClientUserSetup:accountNames.title')} className='isRequired' style={{ flex: 1 }}>
          <Controller
            name='accountNames'
            control={control}
            render={({ field }) => (
              <Flex align='center' gap={16}>
                <CustomSelect
                  {...field}
                  {...sharedProps}
                  mode='multiple'
                  style={{ width: '100%' }}
                  status={errors?.accountNames ? 'error' : ''}
                  placeholder={t('ClientUserSetup:accountNames.placeholder')}
                  options={loading ? [] : accountList}
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                      <span>+{omittedValues?.length}</span>
                    </Tooltip>
                  )}
                  showSearch
                  filterOption={filterOption}
                  loading={loading}
                />
                <Button
                  type='primary'
                  disabled={loading}
                  style={{ height: 42, borderRadius: 12 }}
                  onClick={handleAssignAllExisting}
                >
                  {t('ClientUserSetup:assignAllExisting')}
                </Button>
              </Flex>
            )}
          />

          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='accountNames' />
          </Typography>
        </Form.Item>

        <Controller
          name='assignAll'
          control={control}
          render={({ field }) => (
            <Flex justify='space-between' align='center'>
              <Checkbox
                {...field}
                checked={field.value}
                onChange={(e: CheckboxChangeEvent) => {
                  setValue('assignAll', e.target.checked)
                }}
              >
                {t('ClientUserSetup:assignAll')}
              </Checkbox>
              <Button
                danger
                type='primary'
                disabled={loading}
                style={{ height: 42, borderRadius: 12 }}
                onClick={handleUnassignAllExisting}
              >
                {t('ClientUserSetup:unassignAllExisting')}
              </Button>
            </Flex>
          )}
        />
      </Flex>
    </Flex>
  )
}

export default SelectClient
